import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRightLeft, AlertTriangle, Info, Bot, CheckCircle2,
} from "lucide-react";

/**
 * v6.1 Phase 2c — Bulk Remap.
 *
 * VC-Admin-only. Reach it from:
 *  - Agent Library (needs-attention filter shows a "Bulk Remap" action)
 *  - Individual Agent screen (when other agents share the same broken mapping)
 *  - Settings > AI Keys > provider detail (Remap All Agents, Remap These First)
 *  - Or navigate to /bulk-remap directly to pick a source combo.
 *
 * URL params: ?src_provider=<id>&src_model=<name>  (pre-select)
 */
export default function BulkRemap() {
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [combos, setCombos] = useState([]);
  const [providers, setProviders] = useState([]);
  const [creds, setCreds] = useState([]);
  const [viewingLog, setViewingLog] = useState(null); // when ?remap=<id>
  const [src, setSrc] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState({}); // {agent_id: true}
  const [dst, setDst] = useState({ provider_id: "", model_id: "" });
  const [dstModels, setDstModels] = useState([]);
  const [applying, setApplying] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = async () => {
    const [{ data: c }, { data: p }, { data: cr }] = await Promise.all([
      api.get("/v61/bulk-remap/summary"),
      api.get("/v61/providers"),
      api.get("/v61/credentials"),
    ]);
    setCombos(c);
    setProviders(p);
    setCreds(cr);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const remap = params.get("remap");
    if (remap) {
      api.get(`/v61/bulk-remap-logs/${remap}`)
        .then(({ data }) => setViewingLog(data))
        .catch((e) => toast.error(e?.response?.data?.detail || "Not found"));
      return;
    }
    if (!params.get("src_provider") || !params.get("src_model")) return;
    setSrc({
      provider_id: params.get("src_provider"),
      model_id: params.get("src_model"),
    });
  }, [params]);

  useEffect(() => {
    if (!src) { setAgents([]); return; }
    api.get("/v61/bulk-remap/agents", {
      params: { old_provider_id: src.provider_id, old_model_id: src.model_id },
    }).then(({ data }) => {
      setAgents(data);
      const initial = {};
      data.forEach((a) => { initial[a.agent_id] = true; });
      setSelected(initial);
    });
  }, [src]);

  useEffect(() => {
    if (!dst.provider_id) { setDstModels([]); return; }
    api.get("/v61/models", { params: { provider_id: dst.provider_id } })
      .then(({ data }) => setDstModels(data.models || []));
  }, [dst.provider_id]);

  const providerName = (pid) => providers.find((p) => p.provider_id === pid)?.name || "?";
  const credFor = (pid) => creds.find((c) => c.provider_id === pid);
  const isProviderDisabled = (pid) => {
    const c = credFor(pid);
    return !c || c.status === "invalid" || c.status === "quota_exceeded";
  };
  const disabledReason = (pid) => {
    const c = credFor(pid);
    if (!c) return "No API key configured";
    if (c.status === "invalid") return "API key is invalid";
    if (c.status === "quota_exceeded") return "Billing issue on this provider";
    return "";
  };

  const checkedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected]
  );

  const needsDocReading = agents.some((a) => selected[a.agent_id] && a.requires_document_reading);

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/v61/bulk-remap/apply", {
        old_provider_id: src.provider_id,
        old_model_id: src.model_id,
        new_provider_id: dst.provider_id,
        new_model_id: dst.model_id,
        agent_ids: agents.filter((a) => selected[a.agent_id]).map((a) => a.agent_id),
      });
      toast.success(`Remapped ${data.affected_agent_count} agent${data.affected_agent_count === 1 ? "" : "s"}`);
      setConfirm(false);
      // Return to Agent Library
      nav("/agents");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setApplying(false); }
  };

  return (
    <div className="p-8" data-testid="bulk-remap-page">
      <button onClick={() => nav("/agents")}
        className="text-xs inline-flex items-center gap-1 text-[var(--cw-muted)] hover:text-[var(--cw-primary)]"
        data-testid="bulk-remap-back">
        <ArrowLeft size={12} /> Back to Agents
      </button>
      <h1 className="cw-h1 mt-2 inline-flex items-center gap-2">
        <ArrowRightLeft size={24} /> Bulk Remap
      </h1>
      <p className="text-sm text-[var(--cw-muted)] mt-1 max-w-3xl">
        Move every Agent currently on one provider/model over to a new one, in
        one atomic action. Available reasons include: a model deprecated by
        the provider, a key being deleted, or a deliberate migration to a
        newer model.
      </p>

      {viewingLog && (
        <div className="mt-6 cw-card p-5" data-testid="bulk-remap-log-view">
          <div className="cw-caption uppercase tracking-widest">Bulk Remap Record</div>
          <div className="text-sm mt-2">
            <strong>{viewingLog.old_provider_name}</strong> — <code>{viewingLog.old_model_id}</code>{" "}
            <ArrowRightLeft size={12} className="inline mx-1" />{" "}
            <strong>{viewingLog.new_provider_name}</strong> — <code>{viewingLog.new_model_id}</code>
          </div>
          <div className="text-xs text-[var(--cw-muted)] mt-1">
            Performed by {viewingLog.performed_by_name || "—"} on{" "}
            {new Date(viewingLog.performed_at).toLocaleString()}
          </div>
          <div className="mt-3">
            <div className="text-xs font-semibold mb-1">
              {viewingLog.agents.length} Agent{viewingLog.agents.length === 1 ? "" : "s"} changed together
            </div>
            <ul className="text-sm list-disc ml-5 space-y-0.5">
              {viewingLog.agents.map((a) => (
                <li key={a.agent_id}>
                  <a className="hover:underline text-[var(--cw-primary)]"
                    href={`/agents/${a.agent_id}`}>
                    {a.agent_name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!src && !viewingLog && (
        <div className="mt-6 cw-card p-5">
          <h3 className="cw-h3">Choose a source combination</h3>
          <p className="text-xs text-[var(--cw-muted)] mt-1">
            Pick which existing provider/model to remap away from.
          </p>
          <div className="mt-3 space-y-2">
            {combos.map((c) => (
              <button
                key={`${c.provider_id}-${c.model_id}`}
                onClick={() => setSrc({ provider_id: c.provider_id, model_id: c.model_id })}
                className="w-full text-left flex items-center gap-3 border rounded-md p-3 hover:shadow-sm"
                style={{ borderColor: "var(--cw-border)" }}
                data-testid={`bulk-src-${c.model_id}`}
              >
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    {c.provider_name} — {c.model_id}
                  </div>
                  <div className="text-xs text-[var(--cw-muted)]">
                    {c.agent_count} agent{c.agent_count === 1 ? "" : "s"} on this mapping
                  </div>
                </div>
                {c.any_needs_attention && (
                  <span
                    className="cw-badge inline-flex items-center gap-1"
                    style={{ background: "rgba(215,60,60,0.12)", color: "#B02B2B" }}
                  >
                    <AlertTriangle size={10} /> Needs Attention
                  </span>
                )}
              </button>
            ))}
            {combos.length === 0 && (
              <div className="text-xs text-[var(--cw-muted)] italic p-2">
                No Agents currently have a provider/model configured.
              </div>
            )}
          </div>
        </div>
      )}

      {src && !viewingLog && (
        <div className="mt-6 space-y-4">
          <div className="cw-card p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="cw-caption uppercase tracking-widest">Currently mapped to</div>
                <div className="text-sm font-semibold mt-1">
                  {providerName(src.provider_id)} — {src.model_id}
                </div>
              </div>
              <button
                onClick={() => setSrc(null)}
                className="cw-btn-ghost text-xs"
                data-testid="bulk-src-change"
              >
                Change source
              </button>
            </div>

            <div
              className="mt-3 p-3 rounded flex items-start gap-2"
              style={{
                background: "rgba(243,191,92,0.14)",
                border: "1px solid rgba(243,191,92,0.35)",
              }}
              data-testid="bulk-possible-reasons"
            >
              <Info size={14} className="mt-0.5" />
              <div className="text-xs">
                <div className="font-semibold">Possible reasons this needs remapping</div>
                <ul className="list-disc ml-5 mt-1 space-y-0.5 text-[#7A5900]">
                  <li>The specific model has been withdrawn or deprecated by the provider</li>
                  <li>Your workspace or personal token cap has been reached</li>
                  <li>A billing or quota issue on the provider account behind the key</li>
                  <li>A transient failure on the provider — retry may still succeed</li>
                </ul>
                <div className="mt-2 text-[#7A5900]">
                  If a specific issue persists, check that Agent's Recent Failures for the
                  categorised detail, or contact your VC Admin / the ProdZen team.
                </div>
              </div>
            </div>
          </div>

          <div className="cw-card p-5">
            <h3 className="cw-h3">Affected Agents</h3>
            <p className="text-xs text-[var(--cw-muted)] mt-1">
              Every row is selected by default. Uncheck any Agent to leave it on its current mapping.
            </p>
            <div className="mt-3 space-y-1">
              {agents.map((a) => (
                <label
                  key={a.agent_id}
                  className="flex items-center gap-3 border rounded-md p-2"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid={`bulk-agent-${a.agent_name}`}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[a.agent_id]}
                    onChange={(e) =>
                      setSelected((s) => ({ ...s, [a.agent_id]: e.target.checked }))
                    }
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium inline-flex items-center gap-2">
                      <Bot size={12} /> {a.agent_name}
                    </div>
                    <div className="text-[11px] text-[var(--cw-muted)]">
                      {a.agent_type} · {a.is_platform ? "Platform" : "Custom"}
                      {a.requires_document_reading ? " · doc-reading" : ""}
                      {a.status ? ` · ${a.status}` : ""}
                    </div>
                  </div>
                </label>
              ))}
              {agents.length === 0 && (
                <div className="text-xs text-[var(--cw-muted)] italic">
                  No Agents match this mapping.
                </div>
              )}
            </div>
          </div>

          <div className="cw-card p-5">
            <h3 className="cw-h3">New provider &amp; model</h3>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <div className="cw-caption">New Provider</div>
                <select
                  value={dst.provider_id}
                  onChange={(e) => setDst({ provider_id: e.target.value, model_id: "" })}
                  className="w-full h-9 px-3 rounded-md border text-sm mt-1"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid="bulk-dst-provider"
                >
                  <option value="">Select a provider…</option>
                  {providers.map((p) => {
                    const disabled = isProviderDisabled(p.provider_id);
                    return (
                      <option key={p.provider_id} value={p.provider_id} disabled={disabled}>
                        {p.name}{disabled ? ` — ${disabledReason(p.provider_id)}` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <div className="cw-caption">New Model</div>
                <select
                  value={dst.model_id}
                  onChange={(e) => setDst({ ...dst, model_id: e.target.value })}
                  disabled={!dst.provider_id}
                  className="w-full h-9 px-3 rounded-md border text-sm mt-1 disabled:opacity-50"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid="bulk-dst-model"
                >
                  <option value="">Select a model…</option>
                  {dstModels.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
            {needsDocReading && (
              <div className="text-[11px] text-[var(--cw-muted)] mt-2">
                One or more selected Agents read documents. Confirm the destination model
                supports document input before applying.
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              disabled={!dst.provider_id || !dst.model_id || checkedCount === 0}
              onClick={() => setConfirm(true)}
              className="cw-btn-primary text-sm inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="bulk-apply-btn"
            >
              <ArrowRightLeft size={14} /> Apply to {checkedCount} Agent{checkedCount === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="cw-card p-6 w-full max-w-lg">
            <h3 className="cw-h3 inline-flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#B02B2B]" /> Confirm Bulk Remap
            </h3>
            <p className="text-sm text-[var(--cw-muted)] mt-2">
              We'll test the destination once before applying. If the test succeeds,
              <strong> {checkedCount}</strong> Agent{checkedCount === 1 ? "" : "s"} will move to{" "}
              <strong>{providerName(dst.provider_id)}</strong> — <code>{dst.model_id}</code>.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setConfirm(false)} className="cw-btn-ghost text-sm">Cancel</button>
              <button onClick={apply} disabled={applying}
                className="cw-btn-primary text-sm inline-flex items-center gap-1"
                data-testid="bulk-apply-confirm">
                <CheckCircle2 size={13} /> {applying ? "Applying…" : "Apply Bulk Remap"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
